import { useCallback, useEffect, useState } from 'react';
import { getErrorMessage } from '../api/client';
import { getShiftCloseFormsStatus, startSubmission } from '../api/formSubmissions';
import { FormRunner } from './FormRunner';
import type { ShiftCloseFormItem } from '../types/formSubmissions';

// Fase 23B: seccion "Checklist del turno" dentro del flujo de Corte. Muestra los formularios
// REQUIRED/OPTIONAL de la tienda y su avance; permite responderlos (contexto SHIFT_CLOSE) y reporta al
// padre si los REQUIRED estan completos y que submissions SUBMITTED ligar. El backend revalida al
// cerrar (autoritativo): esto es solo experiencia/guia.
// `startAt` (inicio del turno): acota el checklist a respuestas de ESTE turno. Sin el, una respuesta
// vieja sin vincular marcaria "Completado" en cualquier fecha (defecto historico). El backend revalida.
export function ShiftCloseChecklist({ storeId, startAt, onStatusChange }: { storeId: string; startAt?: string; onStatusChange: (s: { requiredComplete: boolean; submittedIds: string[]; hasForms: boolean }) => void }) {
  const [forms, setForms] = useState<ShiftCloseFormItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>();
  const [configError, setConfigError] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true); setError(undefined);
    try {
      const s = await getShiftCloseFormsStatus(storeId, startAt);
      setForms(s.forms);
      setConfigError(s.hasConfigError);
      onStatusChange({ requiredComplete: s.requiredComplete, submittedIds: s.forms.filter((f) => f.submissionStatus === 'SUBMITTED' && f.submissionId).map((f) => f.submissionId as string), hasForms: s.forms.length > 0 });
    } catch (e) { setError(getErrorMessage(e, 'No se pudo cargar el checklist del turno.')); }
    finally { setLoading(false); }
  }, [storeId, startAt, onStatusChange]);

  useEffect(() => { void load(); }, [load]);

  const respond = async (item: ShiftCloseFormItem) => {
    setError(undefined);
    try {
      const res = await startSubmission({ templateId: item.templateId, storeId, context: 'SHIFT_CLOSE' });
      setOpenId(res.submission.id);
    } catch (e) { setError(getErrorMessage(e, 'No se pudo iniciar el formulario.')); }
  };

  // Fix UX del Corte: al ENVIAR el formulario se regresa solo al corte (sin la pantalla "listo → Volver"),
  // con el checklist ya en "Completado ✓" y "Guardar corte" a la mano. onBack sigue para cancelar/salir.
  if (openId) return <div className="shift-checklist__runner"><FormRunner submissionId={openId} onBack={() => { setOpenId(null); void load(); }} onSubmitted={() => { setOpenId(null); void load(); }} /></div>;

  if (loading) return <div className="inline-loading"><span className="spinner" />Cargando checklist…</div>;
  // P-078 (E · BK33): si el ÚNICO formulario obligatorio está mal configurado, el servidor lo excluye de la lista y
  // `requiredComplete` queda en falso: «Guardar corte» se bloqueaba sin ninguna explicación porque aquí no se pintaba
  // nada. La alerta (y un error de carga) se muestran aunque la lista venga vacía; sin formularios ni problemas, nada.
  const configAlert = configError && <div className="alert alert--warning" role="alert">Hay un formulario obligatorio mal configurado (inactivo o sin versión publicada). Corrígelo en Configuración → Diseño de formularios antes de cerrar.</div>;
  if (forms.length === 0) {
    if (!configError && !error) return null; // sin formularios de corte: no se muestra nada (compatibilidad)
    return (
      <section className="shift-checklist" aria-label="Checklist del turno">
        <h3>Checklist del turno</h3>
        {error && <div className="alert alert--error" role="alert">{error}</div>}
        {configAlert}
      </section>
    );
  }

  return (
    <section className="shift-checklist" aria-label="Checklist del turno">
      <h3>Checklist del turno</h3>
      {error && <div className="alert alert--error" role="alert">{error}</div>}
      {configAlert}
      <ul className="shift-checklist__list">
        {forms.map((f) => (
          <li key={f.templateId} className="shift-checklist__item">
            <div>
              <strong>{f.name}</strong>
              {/* P-075: «Obligatorio» tenía el mismo ámbar que «En progreso» y se confundían en el mismo renglón;
                  ahora usa el tono de atención de la familia `form-badge` (bloquea guardar el corte). */}
              <span className={`form-badge ${f.requirement === 'REQUIRED' ? 'form-badge--attention' : 'form-badge--inactive'}`}>{f.requirement === 'REQUIRED' ? 'Obligatorio' : 'Opcional'}</span>
            </div>
            <div className="shift-checklist__status">
              {f.submissionStatus === 'SUBMITTED' ? <span className="form-badge form-badge--active">Completado ✓</span>
                : f.submissionStatus === 'DRAFT' ? <span className="form-badge form-badge--draft">En progreso</span>
                : <span className="form-badge form-badge--inactive">Pendiente</span>}
              {f.submissionStatus !== 'SUBMITTED' && <button className="button button--secondary button--small" onClick={() => void respond(f)} type="button">{f.submissionStatus === 'DRAFT' ? 'Continuar' : 'Responder'}</button>}
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}
